import * as React from 'react';
import { Check, Loader2, TriangleAlert, Wrench } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export type ToolState = 'input-streaming' | 'input-available' | 'output-available' | 'output-error' | string;

export interface ToolProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Nome técnico (`updateSegmentationDraft`) ou rótulo já amigável. */
  name: string;
  state?: ToolState;
}

const STATE_LABEL: Record<string, string> = {
  'input-streaming': 'Preparando…',
  'input-available': 'Executando…',
  'output-available': 'Concluído',
  'output-error': 'Falhou',
};

function StateIcon({ state }: { state?: ToolState }) {
  if (state === 'output-error') return <TriangleAlert className="size-3.5 text-destructive" />;
  if (state === 'output-available') return <Check className="size-3.5 text-success" />;
  if (state === 'input-streaming' || state === 'input-available') {
    return <Loader2 className="size-3.5 animate-spin text-muted-foreground" />;
  }
  return <Wrench className="size-3.5 text-muted-foreground" />;
}

/** Card de execução de tool — padrão de harness (Cursor/Claude). Tools novas caem aqui sem UI extra. */
export const Tool = React.forwardRef<HTMLDivElement, ToolProps>(
  ({ name, state, className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'w-full overflow-hidden rounded-xl border border-border/70 bg-[hsl(var(--glass-bg)/0.45)] text-sm shadow-glass-sm',
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
        <StateIcon state={state} />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground">{name}</span>
        <span className="shrink-0">{STATE_LABEL[state ?? ''] ?? state ?? ''}</span>
      </div>
      {children ? <div className="border-t border-border/50 px-3 py-2.5">{children}</div> : null}
    </div>
  ),
);
Tool.displayName = 'Tool';

/** Converte `tool-updateSegmentationDraft` → rótulo legível; override via `labels`. */
export function toolDisplayName(type: string, labels?: Record<string, string>): string {
  const key = type.startsWith('tool-') ? type.slice(5) : type;
  if (labels?.[type] || labels?.[key]) return labels[type] ?? labels[key]!;
  return key.replace(/([a-z])([A-Z])/g, '$1 $2');
}
